/* eslint-disable max-len */
import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Box, Button, FormControl, FormLabel, Grid, Checkbox, MenuItem, Select,
} from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TextField from '@mui/material/TextField';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import DatePicker from '../../components/datePicker';
import { getTimeString } from '../../utils/time';
import {
  Description, StyledCard, StyledInput, StyledText,
} from '../../components/styledComponents';
import './RequestModal.css'; // Add this import for custom styles

const moment = require('moment-timezone');

function RequestModal({
  closeModal, policies, setLoading, teamMembers, isManagerInitiated,
}) {
  const { member, activeWorkspace, user } = useContext(UserContext);
  const {
    displayNotification,
  } = useContext(NotificationContext);

  const getHours = (value) => {
    const inputs = value.split(':');
    if (inputs[0] && inputs[1]) {
      return (parseInt(inputs[0], 10) * 60 + parseInt(inputs[1], 10));
    }
    return 0;
  };

  const [selectedMember, setSelectedMember] = useState(
    teamMembers && teamMembers.length > 0 ? teamMembers[0].id : member.id,
  );
  const [memberPolicies, setMemberPolicies] = useState([]);
  const [policy, setPolicy] = useState(0);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [allDay, setAllDay] = useState(true);

  const [ptoValue, setPtoValue] = useState('1.0');
  const [memberBalances, setMemberBalances] = useState({});

  const [totalHours, setTotalHours] = useState('07:30');
  const [reason, setReason] = useState('');
  const [currentBalance, setCurrentBalance] = useState(getTimeString(0));
  const [pendingHours, setPendingHours] = useState(getTimeString(0));
  const [remaining, setRemaining] = useState(getTimeString(0));
  const [selectedTimezone, setSelectedTimezone] = useState(
    activeWorkspace?.timezone || user?.timezone || 'UTC',
  );
  const [startTime, setStartTime] = useState(dayjs().hour(9).minute(0).second(0));
  const [endTime, setEndTime] = useState(dayjs().hour(16).minute(30).second(0));

  const handleAllDayChange = (checked) => {
    setAllDay(checked);

    if (checked) {
      setPtoValue('1');
      setTotalHours('07:30');
      setEndDate(startDate);
    } else {
      if (startTime && endTime) {
        const diffMinutes = endTime.diff(startTime, 'minutes');
        const diffHours = diffMinutes / 60;

        if (diffHours > 0) {
          setPtoValue(diffHours.toFixed(2));
          const hoursStr = Math.floor(diffHours).toString().padStart(2, '0');
          const minutesStr = (diffMinutes % 60).toString().padStart(2, '0');
          setTotalHours(`${hoursStr}:${minutesStr}`);
        } else {
          setPtoValue('0');
          setTotalHours('00:00');
        }
      } else {
        setPtoValue('0');
        setTotalHours('00:00');
      }
      setEndDate(startDate);
    }
  };
  const handleStartTimeChange = (newValue) => {
    setStartTime(newValue);

    if (!allDay && newValue) {
      const hours = parseFloat(ptoValue || 0);
      if (hours > 0) {
        const newEndTime = newValue.add(hours, 'hour');
        setEndTime(newEndTime);
      }
    }
  };

  const handleEndTimeChange = (newValue) => {
    setEndTime(newValue);
    if (!allDay && startTime && newValue) {
      const diffHours = newValue.diff(startTime, 'minutes') / 60;

      if (diffHours > 0) {
        setPtoValue(diffHours.toFixed(2));
        const hoursStr = Math.floor(diffHours).toString().padStart(2, '0');
        const minutesStr = Math.floor((diffHours % 1) * 60).toString().padStart(2, '0');
        setTotalHours(`${hoursStr}:${minutesStr}`);
      }
    }
  };

  const handlePtoValueChange = (event) => {
    const { value } = event.target;
    setPtoValue(value);

    if (allDay) {
      const hours = parseFloat(value || 0) * 7.5;
      const hoursStr = Math.floor(hours).toString().padStart(2, '0');
      const minutesStr = Math.floor((hours % 1) * 60).toString().padStart(2, '0');
      setTotalHours(`${hoursStr}:${minutesStr}`);

      const days = parseInt(value || 1, 10) - 1;
      const newEndDate = new Date(startDate);
      newEndDate.setDate(startDate.getDate() + days);
      setEndDate(newEndDate);
    } else {
      const hours = parseFloat(value || 0);
      const hoursStr = Math.floor(hours).toString().padStart(2, '0');
      const minutesStr = Math.floor((hours % 1) * 60).toString().padStart(2, '0');
      setTotalHours(`${hoursStr}:${minutesStr}`);

      if (startTime && hours > 0) {
        const newEndTime = startTime.add(hours, 'hour');
        setEndTime(newEndTime);
      }
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);

    if (allDay) {
      const days = parseInt(ptoValue || 1, 10) - 1;
      const newEndDate = new Date(date);
      newEndDate.setDate(date.getDate() + days);
      setEndDate(newEndDate);
    } else {
      const timeDiff = Math.abs(endDate.getTime() - date.getTime());
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;

      if (daysDiff > 0) {
        setAllDay(true);
        setPtoValue(daysDiff.toString());
        const hours = daysDiff * 7.5;
        const hoursStr = Math.floor(hours).toString().padStart(2, '0');
        const minutesStr = Math.floor((hours % 1) * 60).toString().padStart(2, '0');
        setTotalHours(`${hoursStr}:${minutesStr}`);
      }
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);

    const timeDiff = Math.abs(date.getTime() - startDate.getTime());
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;

    if (daysDiff > 0) {
      setAllDay(true);
      setPtoValue(daysDiff.toString());
      const hours = daysDiff * 7.5;
      const hoursStr = Math.floor(hours).toString().padStart(2, '0');
      const minutesStr = Math.floor((hours % 1) * 60).toString().padStart(2, '0');
      setTotalHours(`${hoursStr}:${minutesStr}`);
    }
  };

  useEffect(() => {
    if (allDay) {
      setPtoValue('1');
      setTotalHours('07:30');
    } else {
      setPtoValue('7.50');
      setTotalHours('07:30');
    }
  }, []);

  // Fetch policies and balances when member changes
  useEffect(() => {
    const fetchMemberData = async () => {
      const targetMemberId = isManagerInitiated ? selectedMember : member.id;
      if (!targetMemberId || !member.workspaceId) return;

      // Fetch policies for the selected member
      const policyResponse = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getOffTimeData(member.workspaceId, targetMemberId),
      });

      if (policyResponse.data && policyResponse.data.ptoPolicies) {
        setMemberPolicies(policyResponse.data.ptoPolicies);
        if (policyResponse.data.ptoPolicies.length > 0) {
          setPolicy(policyResponse.data.ptoPolicies[0].id);
        }
      }

      // Fetch balances for the selected member
      const balanceResponse = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getPTOBalances(member.workspaceId, targetMemberId),
      });

      if (balanceResponse.success) {
        setMemberBalances(balanceResponse.data.balanceHours || {});

        if (balanceResponse.data.pendingHours !== undefined) {
          setPendingHours(getTimeString(balanceResponse.data.pendingHours / 60));
        } else {
          setPendingHours(getTimeString(0));
        }
      } else {
        setMemberBalances({});
      }
    };
    fetchMemberData();
  }, [isManagerInitiated, selectedMember, member]);

  useEffect(() => {
    const requested = getHours(totalHours);

    let balanceData = null;
    if (memberBalances) {
      balanceData = memberBalances[policy] || memberBalances[String(policy)];
    }

    if (balanceData) {
      const accruedMinutes = (balanceData.accruedHours || 0) / 60;
      const carriedOverMinutes = (balanceData.carriedOverHours || 0) / 60;
      const usedMinutes = (balanceData.usedHours || 0) / 60;

      const currentBalanceMinutes = accruedMinutes + carriedOverMinutes - usedMinutes;
      setCurrentBalance(getTimeString(currentBalanceMinutes));
      const pending = getHours(pendingHours);
      const remainingMinutes = currentBalanceMinutes - requested - pending;
      setRemaining(getTimeString(remainingMinutes));
    } else {
      setCurrentBalance(getTimeString(0));
      const pending = getHours(pendingHours);
      setRemaining(getTimeString(0 - pending - requested));
    }
  }, [policy, totalHours, memberBalances, pendingHours]);

  const handleSubmit = async () => {
    const requestedMinutes = getHours(totalHours);

    if (requestedMinutes <= 0) {
      displayNotification({
        message: 'Total Time Off must be greater than 0.',
        severity: 'error',
      });
      return;
    }

    if (!reason) {
      displayNotification({
        message: 'Time Off reason is required.',
        severity: 'error',
      });
      return;
    }

    if (remaining.includes('-')) {
      displayNotification({
        message: 'Requested time exceeds remaining balance.',
        severity: 'error',
      });
      return;
    }

    let submitStartDate; let
      submitEndDate;

    if (allDay) {
      submitStartDate = startDate;
      submitEndDate = endDate;
    } else {
      const selectedDateStr = moment(startDate).format('YYYY-MM-DD');
      const startTimeStr = dayjs(startTime).format('HH:mm:ss');
      const endTimeStr = dayjs(endTime).format('HH:mm:ss');

      submitStartDate = `${selectedDateStr}T${startTimeStr}`;
      submitEndDate = `${selectedDateStr}T${endTimeStr}`;
    }

    const submitData = {
      policyId: policy,
      totalHours: requestedMinutes,
      startDate: submitStartDate,
      endDate: submitEndDate,
      allDay,
      status: 'Submitted',
      reason,
      timezone: selectedTimezone,
      initiatedByManager: isManagerInitiated || false,
    };

    if (member) {
      const targetMemberId = isManagerInitiated ? selectedMember : member.id;
      if (setLoading) setLoading(true);
      const response = await sendRequest({
        method: 'post',
        url: API.WORKSPACE.createOffTimeRequest(member.workspaceId, targetMemberId),
        data: submitData,
      });

      if (response.success) {
        const successMessage = isManagerInitiated
          ? 'Time off request created and auto-approved successfully.'
          : 'Time off request submitted successfully.';
        displayNotification({
          message: successMessage,
          severity: 'success',
        });
        closeModal();
      } else {
        displayNotification({
          message: 'An error occurred while submitting the request.',
          severity: 'error',
        });
      }
      if (setLoading) setLoading(false);
    }
  };

  return (
    <div className="request-modal-container">
      <Modal open onClose={() => closeModal(false)} className="flex-row align-c jstfy-c create-team-modal">
        <Box style={{
          width: 1044,
          padding: 32,
          background: '#fff',
          borderRadius: 8,
        }}
        >
          <div className="modal-body" style={{ padding: 0 }}>
            <Description
              fontSize={20}
              fontWeight={600}
              color="#0E1E40"
              marginTop={0}
            >
              Request Time Off
            </Description>
            <Grid container spacing={3}>
              <Grid item xs={8}>
                {isManagerInitiated && teamMembers && teamMembers.length > 0 && (
                  <>
                    <Description
                      fontSize={14}
                      fontWeight={500}
                      color="#293855"
                      marginTop={12}
                    >
                      Select Member
                    </Description>
                    <FormControl sx={{ width: '100%', marginTop: '12px' }}>
                      <Select
                        value={selectedMember}
                        onChange={(e) => setSelectedMember(e.target.value)}
                      >
                        {teamMembers.map((teamMember) => (
                          <MenuItem
                            key={teamMember.id}
                            value={teamMember.id}
                          >
                            {teamMember.user?.firstName}
                            {' '}
                            {teamMember.user?.lastName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
                <Description
                  fontSize={14}
                  fontWeight={500}
                  color="#293855"
                  marginTop={12}
                >
                  Policy
                </Description>
                <FormControl sx={{ width: '100%', marginTop: '12px' }}>
                  <Select
                    value={policy}
                    onChange={(e) => setPolicy(e.target.value)}
                  >
                    {(isManagerInitiated ? memberPolicies : policies).map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Description
                  fontSize={14}
                  fontWeight={500}
                  color="#293855"
                  marginTop={12}
                >
                  Time Span
                </Description>
                <Box sx={{ display: 'flex', alignItems: 'center', margin: '12px 0px' }}>
                  <DatePicker
                    oneTap
                    value={startDate}
                    onChange={handleStartDateChange}
                  />
                  <span style={{ margin: '0px 8px' }}>to</span>
                  <DatePicker
                    oneTap
                    value={endDate}
                    onChange={handleEndDateChange}
                  />
                </Box>

                {!allDay && (
                  <>
                    <Description
                      fontSize={14}
                      fontWeight={500}
                      color="#293855"
                      marginTop={12}
                    >
                      Time Range
                    </Description>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 2, margin: '12px 0px',
                    }}
                    >
                      <div className="d-flex gap-2 align-items-center">
                        <div style={{ fontSize: '12px', color: '#666' }}>From</div>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            ampm={false}
                            label=""
                            value={startTime}
                            onChange={handleStartTimeChange}
                            renderInput={(params) => (
                              <TextField
                             /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...params}
                                variant="outlined"
                                size="small"
                                sx={{ width: 140 }}
                              />
                            )}
                          />
                        </LocalizationProvider>

                        <div style={{ fontSize: '12px', color: '#666' }}>To</div>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            ampm={false}
                            label=""
                            value={endTime}
                            onChange={handleEndTimeChange}
                            renderInput={(params) => (
                              <TextField
                             /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...params}
                                variant="outlined"
                                size="small"
                                sx={{
                                  width: 140,
                                  '& .MuiInputBase-input': {
                                    backgroundColor: '#f9f9f9',
                                  },
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </div>
                    </Box>
                  </>
                )}

                <Description
                  fontSize={14}
                  fontWeight={500}
                  color="#293855"
                  marginTop={12}
                >
                  Timezone
                </Description>
                <FormControl sx={{ width: '100%', marginTop: '12px' }}>
                  <Select
                    value={selectedTimezone}
                    onChange={(e) => setSelectedTimezone(e.target.value)}
                  >
                    <MenuItem value={selectedTimezone}>
                      Your Time Zone :
                      {selectedTimezone}
                    </MenuItem>
                    {activeWorkspace?.timezone && (
                      <MenuItem value={activeWorkspace.timezone}>{activeWorkspace.timezone}</MenuItem>
                    )}
                    {user?.timezone && user.timezone !== activeWorkspace?.timezone && (
                      <MenuItem value={user.timezone}>{user.timezone}</MenuItem>
                    )}
                    <MenuItem value="UTC">UTC</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ width: '100%', marginTop: '16px', marginBottom: '8px' }}>
                  <FormLabel
                    sx={{
                      color: '#293855',
                      fontSize: '14px',
                      marginBottom: '8px',
                    }}
                  >
                    Request Type:
                  </FormLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        sx={{ padding: '4px 8px 4px 0px' }}
                        onChange={(e) => handleAllDayChange(e.target.checked)}
                        checked={allDay}
                      />
                      <Description fontSize={14} fontWeight={600}>
                        Full Days (7.5 hours per day)
                      </Description>
                    </Box>
                    {!allDay && (
                      <Description fontSize={12} fontWeight={400} color="#666" marginLeft={2}>
                        Unchecked = Request specific hours
                      </Description>
                    )}
                  </Box>
                </FormControl>

                <FormControl sx={{ width: '100%' }}>
                  <FormLabel
                    sx={{
                      color: '#293855',
                      fontSize: '14px',
                    }}
                  >
                    {allDay ? 'Number of Days' : 'Number of Hours'}
                  </FormLabel>
                  <StyledInput
                    type="number"
                    onChange={handlePtoValueChange}
                    value={ptoValue}
                    inputProps={{
                      min: allDay ? 0.5 : 0.25,
                      step: allDay ? 0.5 : 0.25,
                    }}
                  />
                  <Description
                    fontSize={12}
                    fontWeight={400}
                    color="#666"
                    marginTop={4}
                  >
                    {allDay
                      ? `${ptoValue ? (parseFloat(ptoValue) * 7.5).toFixed(1) : 0} hours total (1 day = 7.5 hours)`
                      : 'Enter hours (e.g., 4.5 for 4 hours 30 minutes)'}
                  </Description>
                </FormControl>

                <FormControl sx={{ width: '100%' }}>
                  <FormLabel
                    sx={{
                      color: '#293855',
                      fontSize: '14px',
                    }}
                  >
                    Total Time Off Hours
                  </FormLabel>
                  <StyledInput
                    onChange={(e) => setTotalHours(e.target.value)}
                    value={totalHours}
                    disabled
                  />
                </FormControl>

                <FormControl sx={{ width: '100%' }}>
                  <FormLabel
                    sx={{
                      color: '#293855',
                      fontSize: '14px',
                    }}
                  >
                    Time Off Reason
                  </FormLabel>
                  <StyledText onChange={(e) => setReason(e.target.value)} value={reason} placeholder="Required" />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ paddingLeft: '24px', paddingTop: '38px', borderLeft: '1px solid #E0E1E6' }}>
                  <Box sx={{ paddingBottom: '16px', borderBottom: '1px solid #E0E1E6' }}>

                    <StyledCard>
                      <span>Current Balance</span>
                      <span style={{
                        color: currentBalance.includes('-') ? '#d32f2f' : '#333',
                        fontWeight: '600',
                      }}
                      >
                        {currentBalance}
                      </span>
                    </StyledCard>

                    <StyledCard>
                      <span>
                        {allDay ? 'Days' : 'Hours'}
                        {' '}
                        Requested
                      </span>
                      <span style={{ fontWeight: '600', color: '#1976d2' }}>
                        {allDay
                          ? `${ptoValue} day${parseFloat(ptoValue) !== 1 ? 's' : ''}`
                          : `${ptoValue} hours`}
                      </span>
                    </StyledCard>

                    <StyledCard key={`pending-${pendingHours}`}>
                      <span>Other Pending Approval</span>
                      <span style={{
                        color: pendingHours !== '00:00' ? '#ff9800' : '#666',
                        fontWeight: '600',
                      }}
                      >
                        {pendingHours}
                      </span>
                    </StyledCard>

                  </Box>

                  <Box sx={{ paddingTop: '16px' }}>
                    <StyledCard>
                      <span style={{ fontSize: '16px', fontWeight: '600' }}>
                        Remaining Balance
                      </span>
                      <span style={{
                        color: remaining.includes('-') ? '#d32f2f' : '#4caf50',
                        fontWeight: 'bold',
                        fontSize: '18px',
                      }}
                      >
                        {remaining}
                      </span>
                    </StyledCard>

                    {remaining.includes('-') && (
                      <Box sx={{
                        marginTop: '8px',
                        padding: '8px',
                        backgroundColor: '#ffebee',
                        borderRadius: '4px',
                        border: '1px solid #ffcdd2',
                      }}
                      >
                        <Description
                          fontSize={12}
                          fontWeight={500}
                          color="#d32f2f"
                          marginTop={0}
                        >
                          ⚠️ This request exceeds your available balance
                        </Description>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </div>
          <div
            className="modal-footer-customized"
            style={{
              display: 'flex',
              justifyContent: 'right',
            }}
          >
            <Button
              sx={{
                marginRight: 2,
                textTransform: 'none',
                fontSize: '16px',
                height: '40px',
                width: '101px',
              }}
              variant="outlined"
              onClick={() => closeModal(false)}
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              sx={{
                textTransform: 'none',
                fontSize: '16px',
                height: '40px',
                width: '154px',
                backgroundColor: 'var(--green-3, #53A551)',
                color: '#FFFFFF',
                '&:hover': { backgroundColor: 'var(--green-3, #53A551)' },
              }}
              variant="contained"
              onClick={() => handleSubmit()}
            >
              Submit Request
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

RequestModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  policies: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })),
  setLoading: PropTypes.func,
  teamMembers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    user: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  })),
  isManagerInitiated: PropTypes.bool,
};

RequestModal.defaultProps = {
  policies: [],
  setLoading: null,
  teamMembers: [],
  isManagerInitiated: false,
};

export default RequestModal;
